import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/lib/auth';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/app');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-6">
      <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-[#1E4E8C]/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-[#0A0E1A]/5 rounded-full blur-[100px]" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative w-full max-w-md">
        <div className="rounded-xl border border-gray-200 bg-white shadow-xl shadow-gray-200/50 p-8">
          <div className="flex items-center gap-3 mb-8">
            <img src="/procurex-logo-light.svg" alt="ProcureX" className="h-9" />
            <div>
              <p className="text-xs text-gray-500">Sign in to your account</p>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs text-gray-600 mb-1.5 block font-medium">Email</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" className="h-10" data-testid="login-email-input" required />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1.5 block font-medium">Password</label>
              <div className="relative">
                <Input type={showPass ? 'text' : 'password'} value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="Enter password"
                  className="h-10 pr-10" data-testid="login-password-input" required />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#1E4E8C] hover:bg-[#163D70] text-white h-10"
              disabled={loading} data-testid="login-submit-btn">
              {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> :
                <>Sign In <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>
          <p className="text-center text-xs text-gray-400 mt-6">
            Access is invite-only. Contact your administrator for an invitation.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
